import { Injectable, inject } from '@angular/core';
import { WriteBatch, collection, doc, writeBatch } from 'firebase/firestore';
import { FIRESTORE } from '../firebase.config';
import {
    Notification,
    NotificationType,
    NotificationPriority,
    ClientCommitment,
    LoanStatus
} from '../models/loan.model';

@Injectable({
    providedIn: 'root'
})
export class NotificationService {

    /**
     * Create a notification with dual-write pattern
     * @param data Notification data (without id)
     * @param batch Optional WriteBatch to include in
     */
    async createNotification(
        data: Omit<Notification, 'id'>,
        batch?: WriteBatch
    ): Promise<void> {
        const useBatch = batch || writeBatch(FIRESTORE);
        const notificationRef = doc(collection(FIRESTORE, 'notifications'));
        const userNotificationRef = doc(FIRESTORE, `users/${data.userId}/notifications/${notificationRef.id}`);

        const notificationData: Notification = {
            id: notificationRef.id,
            ...data
        };

        // Dual-write to top-level and user's subcollection
        useBatch.set(notificationRef, notificationData);
        useBatch.set(userNotificationRef, notificationData);

        // If no batch was provided, commit immediately
        if (!batch) {
            await useBatch.commit();
        }
    }

    /**
     * Send notifications to multiple users
     */
    async createNotifications(
        notifications: Omit<Notification, 'id'>[],
        batch?: WriteBatch
    ): Promise<void> {
        const useBatch = batch || writeBatch(FIRESTORE);

        for (const notificationData of notifications) {
            const notificationRef = doc(collection(FIRESTORE, 'notifications'));
            const userNotificationRef = doc(FIRESTORE, `users/${notificationData.userId}/notifications/${notificationRef.id}`);

            const notification: Notification = {
                id: notificationRef.id,
                ...notificationData
            };

            useBatch.set(notificationRef, notification);
            useBatch.set(userNotificationRef, notification);
        }

        // If no batch was provided, commit immediately
        if (!batch) {
            await useBatch.commit();
        }
    }

    /**
     * Notify clients about deal status changes
     */
    async notifyDealStatusChange(
        dealId: string,
        dealName: string,
        oldStatus: LoanStatus,
        newStatus: LoanStatus,
        userIds: string[],
        batch?: WriteBatch
    ): Promise<void> {
        const statusMessages: Record<string, { message: string; priority: NotificationPriority }> = {
            'DRAFT->ACTIVE': {
                message: `"${dealName}" is now live and accepting commitments.`,
                priority: 'MEDIUM'
            },
            'ACTIVE->FUNDED': {
                message: `"${dealName}" has been fully funded and is now in servicing.`,
                priority: 'HIGH'
            },
            'FUNDED->FINISHED': {
                message: `"${dealName}" has been completed and final reconciliation is underway.`,
                priority: 'HIGH'
            },
            'EXTENDED->FINISHED': {
                message: `"${dealName}" has been completed and final reconciliation is underway.`,
                priority: 'HIGH'
            },
            'DEFAULTED->FINISHED': {
                message: `"${dealName}" has been marked as finished following default status.`,
                priority: 'HIGH'
            }
        };

        const key = `${oldStatus}->${newStatus}`;
        const config = statusMessages[key];

        if (!config) {
            // For other status transitions, use a generic message
            return;
        }

        const now = new Date().toISOString();
        const notifications = userIds.map(userId => ({
            userId,
            dealId,
            dealName,
            type: 'DEAL_STATUS_CHANGE' as NotificationType,
            priority: config.priority,
            message: config.message,
            timestamp: now,
            read: false,
            actionUrl: `/client/deal/${dealId}`,
            metadata: { oldStatus, newStatus }
        }));

        await this.createNotifications(notifications, batch);
    }

    /**
     * Notify client about commitment confirmation
     */
    async notifyCommitmentConfirmed(
        commitment: ClientCommitment,
        dealName: string,
        batch?: WriteBatch
    ): Promise<void> {
        const amount = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(commitment.committedAmount);

        await this.createNotification({
            userId: commitment.userId,
            dealId: commitment.loanDealId,
            dealName,
            type: 'COMMITMENT_CONFIRMED',
            priority: 'MEDIUM',
            message: `Your commitment of ${amount} to "${dealName}" has been confirmed by our team.`,
            timestamp: new Date().toISOString(),
            read: false,
            actionUrl: `/client/deal/${commitment.loanDealId}`,
            metadata: { commitmentId: commitment.id, amount: commitment.committedAmount }
        }, batch);
    }

    /**
     * Notify client about funds received confirmation
     */
    async notifyFundsReceived(
        commitment: ClientCommitment,
        dealName: string,
        amount: number,
        batch?: WriteBatch
    ): Promise<void> {
        const formattedAmount = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);

        await this.createNotification({
            userId: commitment.userId,
            dealId: commitment.loanDealId,
            dealName,
            type: 'FUNDS_RECEIVED',
            priority: 'HIGH',
            message: `Your funds of ${formattedAmount} for "${dealName}" have been received and confirmed.`,
            timestamp: new Date().toISOString(),
            read: false,
            actionUrl: `/client/deal/${commitment.loanDealId}`,
            metadata: { commitmentId: commitment.id, amount }
        }, batch);
    }

    /**
     * Notify client about Heads of Terms being signed
     */
    async notifyHoTSigned(
        commitment: ClientCommitment,
        dealName: string,
        batch?: WriteBatch
    ): Promise<void> {
        await this.createNotification({
            userId: commitment.userId,
            dealId: commitment.loanDealId,
            dealName,
            type: 'HOT_SIGNED',
            priority: 'MEDIUM',
            message: `Heads of Terms for your commitment to "${dealName}" have been signed.`,
            timestamp: new Date().toISOString(),
            read: false,
            actionUrl: `/client/deal/${commitment.loanDealId}`,
            metadata: { commitmentId: commitment.id }
        }, batch);
    }

    /**
     * Notify clients about interest distribution
     */
    async notifyInterestDistribution(
        dealId: string,
        dealName: string,
        userIds: string[],
        totalAmount: number,
        period: string,
        batch?: WriteBatch
    ): Promise<void> {
        const now = new Date().toISOString();
        const notifications = userIds.map(userId => ({
            userId,
            dealId,
            dealName,
            type: 'INTEREST_DISTRIBUTION' as NotificationType,
            priority: 'HIGH' as NotificationPriority,
            message: `Interest distribution for "${dealName}" (${period}) has been processed.`,
            timestamp: now,
            read: false,
            actionUrl: `/client/deal/${dealId}`,
            metadata: { period, totalAmount }
        }));

        await this.createNotifications(notifications, batch);
    }

    /**
     * Notify clients about loan extension
     */
    async notifyLoanExtension(
        dealId: string,
        dealName: string,
        userIds: string[],
        newEndDate: string,
        batch?: WriteBatch
    ): Promise<void> {
        const formattedDate = new Date(newEndDate).toLocaleDateString('en-GB');
        const now = new Date().toISOString();

        const notifications = userIds.map(userId => ({
            userId,
            dealId,
            dealName,
            type: 'LOAN_EXTENSION' as NotificationType,
            priority: 'MEDIUM' as NotificationPriority,
            message: `The loan for "${dealName}" has been extended. The new end date is ${formattedDate}.`,
            timestamp: now,
            read: false,
            actionUrl: `/client/deal/${dealId}`,
            metadata: { newEndDate }
        }));

        await this.createNotifications(notifications, batch);
    }

    /**
     * Notify clients about loan default
     */
    async notifyLoanDefault(
        dealId: string,
        dealName: string,
        userIds: string[],
        batch?: WriteBatch
    ): Promise<void> {
        const now = new Date().toISOString();

        const notifications = userIds.map(userId => ({
            userId,
            dealId,
            dealName,
            type: 'LOAN_DEFAULT' as NotificationType,
            priority: 'HIGH' as NotificationPriority,
            message: `IMPORTANT: The loan for "${dealName}" has been marked as 'Defaulted'. Default interest rates now apply.`,
            timestamp: now,
            read: false,
            actionUrl: `/client/deal/${dealId}`,
            metadata: {}
        }));

        await this.createNotifications(notifications, batch);
    }

    /**
     * Notify clients about call for funds
     */
    async notifyCallForFunds(
        dealId: string,
        dealName: string,
        userIds: string[],
        batch?: WriteBatch
    ): Promise<void> {
        const now = new Date().toISOString();

        const notifications = userIds.map(userId => ({
            userId,
            dealId,
            dealName,
            type: 'CALL_FOR_FUNDS' as NotificationType,
            priority: 'HIGH' as NotificationPriority,
            message: `Call for funds: Please transfer your committed funds for "${dealName}" as the loan start date is approaching.`,
            timestamp: now,
            read: false,
            actionUrl: `/client/deal/${dealId}`,
            metadata: {}
        }));

        await this.createNotifications(notifications, batch);
    }

    /**
     * Notify client about redemption being processed
     */
    async notifyRedemptionProcessed(
        dealId: string,
        dealName: string,
        userId: string,
        totalPayout: number,
        batch?: WriteBatch
    ): Promise<void> {
        const formattedAmount = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(totalPayout);

        await this.createNotification({
            userId,
            dealId,
            dealName,
            type: 'REDEMPTION_PROCESSED',
            priority: 'HIGH',
            message: `Your redemption from "${dealName}" has been processed. Total payout: ${formattedAmount}.`,
            timestamp: new Date().toISOString(),
            read: false,
            actionUrl: `/client/deal/${dealId}`,
            metadata: { totalPayout }
        }, batch);
    }

    /**
     * Notify clients about borrower payment received
     */
    async notifyBorrowerPayment(
        dealId: string,
        dealName: string,
        userIds: string[],
        amount: number,
        batch?: WriteBatch
    ): Promise<void> {
        const formattedAmount = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);
        const now = new Date().toISOString();

        const notifications = userIds.map(userId => ({
            userId,
            dealId,
            dealName,
            type: 'BORROWER_PAYMENT' as NotificationType,
            priority: 'MEDIUM' as NotificationPriority,
            message: `Borrower payment of ${formattedAmount} received for "${dealName}".`,
            timestamp: now,
            read: false,
            actionUrl: `/client/deal/${dealId}`,
            metadata: { amount }
        }));

        await this.createNotifications(notifications, batch);
    }

    /**
     * Notify clients about drawdown executed to borrower
     */
    async notifyDrawdownExecuted(
        dealId: string,
        dealName: string,
        userIds: string[],
        amount: number,
        batch?: WriteBatch
    ): Promise<void> {
        const formattedAmount = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);
        const now = new Date().toISOString();

        const notifications = userIds.map(userId => ({
            userId,
            dealId,
            dealName,
            type: 'DRAWDOWN_EXECUTED' as NotificationType,
            priority: 'MEDIUM' as NotificationPriority,
            message: `Funds of ${formattedAmount} have been disbursed to the borrower for "${dealName}".`,
            timestamp: now,
            read: false,
            actionUrl: `/client/deal/${dealId}`,
            metadata: { amount }
        }));

        await this.createNotifications(notifications, batch);
    }

    /**
     * Notify client about commitment received (client-initiated)
     */
    async notifyCommitmentReceived(
        userId: string,
        dealId: string,
        dealName: string,
        amount: number,
        batch?: WriteBatch
    ): Promise<void> {
        const formattedAmount = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);

        await this.createNotification({
            userId,
            dealId,
            dealName,
            type: 'COMMITMENT_RECEIVED',
            priority: 'MEDIUM',
            message: `Your commitment of ${formattedAmount} to "${dealName}" has been received and is awaiting confirmation.`,
            timestamp: new Date().toISOString(),
            read: false,
            actionUrl: `/client/deal/${dealId}`,
            metadata: { amount }
        }, batch);
    }
}
